'use strict'

import Polyfills from './Polyfills.js'
import { Bartender } from '@fokke-/bartender.js'
import boringmenu from '@teppokoivula/boringmenu'
import Glightbox from 'glightbox'

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.1.0
 */
export default class Site {
	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {}

		// Init polyfills
		const polyfills = new Polyfills()
		polyfills.init()

		// Safari detection
		if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
			document.documentElement.classList.add('is-safari')
		}

		// Init off-canvas after mobile menu is ready
		document.addEventListener('boringmenu-init-done', () => {
			window.bartender = new Bartender()

			window.bartender.addBar('mobile-bar', {
				el: document.querySelector('.mobile-bar'),
				position: 'right',
			})

			document.querySelectorAll('.js-toggle-mobile-bar').forEach((el) => {
				el.addEventListener('click', (event) => {
					window.bartender.toggle('mobile-bar', event.currentTarget)
				})
			})

			document.querySelectorAll('.js-close-mobile-bar').forEach((el) => {
				el.addEventListener('click', () => {
					window.bartender.close()
				})
			})
		})

		// Init mobile menu
		window.addEventListener('DOMContentLoaded', () => {
			const mobileMenu = document.getElementById('mobile-menu')
			new boringmenu({
				selectors: {
					menu: '.menu-mobile__list--level-1',
				},
				classes: {
					item: 'menu-mobile__item',
					itemActive: 'menu-mobile__item--current',
					itemParent: 'menu-mobile__item--parent',
					toggle: 'menu-mobile__toggle',
					toggleTextContainer: 'sr-only',
				},
				labels: {
					'menu.open': mobileMenu
						? mobileMenu.getAttribute('data-labels-open')
						: 'open',
					'menu.close': mobileMenu
						? mobileMenu.getAttribute('data-labels-close')
						: 'close',
				},
				icons: {
					'menu.open': 'icon-open',
					'menu.close': 'icon-close',
				},
			})
		})

		// Initialize
		this.init(options)
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table',
			},
			imageLinks: {
				parentSelector: 'main',
			},
			...options,
		}

		// Call individual init methods
		this.initResponsiveTables()
		this.initSkipLinks()
		this.initArticleGallery()
		this.initImageLinks()
		this.initTopMenuScroll()
		this.initSubMenuEdge()
		this.initReactionButtonsFeedback()
		this.initBackToTop()
		this.initAreaSearch()

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true,
			})
		)

		return this
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document
			.querySelectorAll(this.options.responsiveTables.selector)
			.forEach((table) => {
				if (!table.closest('.table-wrapper')) {
					const tableWrapper = document.createElement('div')
					tableWrapper.classList.add('table-wrapper')
					tableWrapper.classList.add('overflow-x-auto')
					table.parentNode.insertBefore(tableWrapper, table)
					tableWrapper.appendChild(table)
				}
			})
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll(
			'.skip-link:not([data-skip-link])'
		)
		if (skipLinks.length) {
			const skipToBlur = (event) => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex')
					event.target.removeAttribute('data-tabindex')
				}
			}
			skipLinks.forEach((skipLink) => {
				skipLink.setAttribute('data-skip-link', true)
				skipLink.addEventListener('click', (event) => {
					const skipTo = document.getElementById(
						event.target.href.split('#')[1]
					)
					if (!skipTo) return
					event.preventDefault()
					const focusTarget = skipTo.querySelector('h1') || skipTo
					if (focusTarget.getAttribute('tabindex') != '-1') {
						focusTarget.setAttribute('tabindex', '-1')
						focusTarget.setAttribute('data-tabindex', true)
						focusTarget.addEventListener('blur', skipToBlur)
						focusTarget.addEventListener('focusout', skipToBlur)
					}
					focusTarget.focus()
				})
			})
		}
	}

	/**
	 * Initialize top menu scroll fade indicator
	 */
	initTopMenuScroll() {
		const list = document.querySelector('.top-menu__list--level-1')
		const fadeLeft = document.querySelector('.top-menu-scroll__fade--left')
		const fadeRight = document.querySelector('.top-menu-scroll__fade--right')
		if (!list || !fadeLeft || !fadeRight) return

		if (this._topMenuScrollCleanup) {
			this._topMenuScrollCleanup()
		}

		const updateFades = () => {
			const atStart = list.scrollLeft <= 2
			const atEnd =
				list.scrollLeft + list.clientWidth >= list.scrollWidth - 2
			fadeLeft.style.opacity = atStart ? '0' : '1'
			fadeLeft.style.pointerEvents = atStart ? 'none' : 'auto'
			fadeRight.style.opacity = atEnd ? '0' : '1'
			fadeRight.style.pointerEvents = atEnd ? 'none' : 'auto'
		}

		list.addEventListener('scroll', updateFades)

		fadeLeft.addEventListener('click', () => {
			list.scrollBy({ left: -200, behavior: 'smooth' })
		})

		fadeRight.addEventListener('click', () => {
			list.scrollBy({ left: 200, behavior: 'smooth' })
		})

		const resizeObserver = new ResizeObserver(updateFades)
		resizeObserver.observe(list)

		this._topMenuScrollCleanup = () => {
			list.removeEventListener('scroll', updateFades)
			resizeObserver.disconnect()
		}

		updateFades()
	}

	/**
	 * Initialize submenu edge detection and fixed positioning
	 */
	initSubMenuEdge () {
		const items = document.querySelectorAll('.top-menu__list-item--has-children')
		if (!items.length) return

		const LAPTOP_BP = 1024

		const positionFixed = (listItem) => {
			const subMenu = listItem.querySelector('.top-menu__list--level-2')
			if (!subMenu || window.innerWidth >= LAPTOP_BP) return

			const parentRect = listItem.getBoundingClientRect()
			let left = parentRect.left

			subMenu.style.top = `${parentRect.bottom}px`
			subMenu.style.left = `${left}px`
			subMenu.style.right = 'auto'

			requestAnimationFrame(() => {
				const menuRect = subMenu.getBoundingClientRect()
				if (menuRect.right > document.documentElement.clientWidth) {
					subMenu.style.left = 'auto'
					subMenu.style.right = '0'
				}
			})
		}

		const checkEdge = (listItem) => {
			const subMenu = listItem.querySelector('.top-menu__list--level-2')
			if (!subMenu || window.innerWidth < LAPTOP_BP) return

			subMenu.classList.remove('top-menu__list--level-2--edge')

			requestAnimationFrame(() => {
				const rect = subMenu.getBoundingClientRect()
				if (rect.right > document.documentElement.clientWidth) {
					subMenu.classList.add('top-menu__list--level-2--edge')
				}
			})
		}

		items.forEach((item) => {
			item.addEventListener('mouseenter', () => {
				positionFixed(item)
				checkEdge(item)
			})
			item.addEventListener('focusin', () => {
				positionFixed(item)
				checkEdge(item)
			})
		})
	}

	/**
	 * Initialize article image gallery
	 */
	initArticleGallery() {
		document.querySelectorAll('[data-article-gallery]').forEach((gallery) => {
			const mainImage = gallery.querySelector('.article-gallery__main-image')
			const mainLink = gallery.querySelector('.article-gallery__main-link')
			const caption = gallery.querySelector('.article-gallery__caption')
			const thumbs = gallery.querySelectorAll('.article-gallery__thumb')
			const dots = gallery.querySelectorAll('.article-gallery__dot')

			// Build GLightbox elements from thumbnails
			const elements = Array.from(thumbs).map((thumb) => ({
				href: thumb.getAttribute('data-gallery-href'),
				title: thumb.getAttribute('data-gallery-caption'),
			}))

			const lightbox = Glightbox({ elements })

			const activate = (index) => {
				const thumb = thumbs[index]
				if (!thumb) return

				mainImage.src = thumb.getAttribute('data-gallery-src')
				mainImage.alt = thumb.getAttribute('data-gallery-alt')
				mainLink.href = thumb.getAttribute('data-gallery-href')
				mainLink.setAttribute('data-gallery-current', index)

				const captionText = thumb.getAttribute('data-gallery-caption')
				if (captionText) {
					caption.textContent = captionText
					caption.removeAttribute('hidden')
				} else {
					caption.textContent = ''
					caption.setAttribute('hidden', '')
				}

				thumbs.forEach((t) => t.classList.remove('article-gallery__thumb--active'))
				thumb.classList.add('article-gallery__thumb--active')

				dots.forEach((d) => d.classList.remove('article-gallery__dot--active'))
				dots[index]?.classList.add('article-gallery__dot--active')
			}

			// Clicking main image opens lightbox at current slide
			mainLink.addEventListener('click', (e) => {
				e.preventDefault()
				const current = parseInt(mainLink.getAttribute('data-gallery-current'), 10)
				lightbox.openAt(current)
			})

			thumbs.forEach((thumb) => {
				thumb.addEventListener('click', () => {
					activate(parseInt(thumb.getAttribute('data-gallery-index'), 10))
				})
			})

			dots.forEach((dot) => {
				dot.addEventListener('click', () => {
					activate(parseInt(dot.getAttribute('data-gallery-index'), 10))
				})
			})
		})
	}

	/**
	 * Initialize image links
	 */
	initImageLinks() {
		// Parent of image links
		let parentNode = document.querySelector(
			this.options.imageLinks.parentSelector
		)
		if (!parentNode) return

		// Add glightbox class to image links
		parentNode
			.querySelectorAll(
				'a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]'
			)
			.forEach((link) => {
				if (!link.classList.contains('glightbox') && !link.closest('[data-article-gallery]')) {
					if (
						!link.getAttribute('data-title') &&
						!link.getAttribute('data-glightbox')
					) {
						let figcaption = link.parentNode.querySelector('figcaption')
						if (figcaption) {
							let caption = figcaption ? figcaption.textContent : ''
							link.setAttribute('data-title', caption)
						}
					}
					link.classList.add('glightbox')
				}
			})

		// Initialize GLightbox
		if (parentNode.querySelector('.glightbox')) {
			window.glightbox = Glightbox()
		}
	}

	/**
	 * Initialize reaction buttons feedback
	 */
	initReactionButtonsFeedback () {
		const reactionButtonsFeedback = document.querySelector('.js-reaction-buttons-feedback')
		const reactionButtonsThanks = document.querySelector('.js-reaction-buttons-thanks')

		if (reactionButtonsFeedback) {
			const iframe = reactionButtonsFeedback.querySelector('iframe')

			let savedScrollY = null

			const reloadIframe = () => {
				if (iframe) {
					savedScrollY = window.scrollY
					iframe.src += ''
				}
			}

			if (iframe) {
				let userHasInteracted = false

				// Save scroll position when user interacts with the iframe (before form submit navigates)
				const markInteracted = () => {
					userHasInteracted = true
					savedScrollY = window.scrollY
				}
				iframe.addEventListener('mouseenter', markInteracted)
				iframe.addEventListener('pointerdown', markInteracted)
				iframe.addEventListener('touchstart', markInteracted)
				iframe.addEventListener('focusin', markInteracted)

				iframe.addEventListener('load', () => {
					// Restore scroll position — browser/FormBuilder scrolls to 0 on iframe navigation
					if (savedScrollY !== null) {
						window.scrollTo({ top: savedScrollY })
						savedScrollY = null
					}
					if (userHasInteracted) {
						try {
							const successEl = iframe.contentDocument.querySelector('.form__success')
							if (successEl) {
								const message = document.createElement('p')
								message.className = 'reactions__feedback-message'
								message.textContent = successEl.textContent
								iframe.replaceWith(message)
							}
						} catch (e) {
							// cross-origin: do nothing
						}
					}
				})
			}

			document.addEventListener('reactions-updated', event => {
				const activeButton = document.querySelector('.reaction-buttons__button--active')
				if (!activeButton.getAttribute('data-feedback-form')) {
					reactionButtonsFeedback.setAttribute('hidden', '')
					if (reactionButtonsThanks) {
						reactionButtonsThanks.removeAttribute('hidden')
					}
				} else if (activeButton.getAttribute('data-feedback-form') && reactionButtonsFeedback.hasAttribute('hidden')) {
					reactionButtonsFeedback.removeAttribute('hidden')
					if (reactionButtonsThanks) {
						reactionButtonsThanks.setAttribute('hidden', '')
					}
					reloadIframe()
				} else {
					if (reactionButtonsThanks) {
						reactionButtonsThanks.setAttribute('hidden', '')
					}
					reloadIframe()
				}
			})
		}
	}

	/**
	 * Initialize back to top button
	 */
	initBackToTop () {
		const button = document.querySelector('.js-back-to-top:not([data-back-to-top])')
		if (button) {
			button.setAttribute('data-back-to-top', true)
			button.addEventListener('click', () => {
				const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
				window.scrollTo({ top: 0, behavior })
			})
		}
	}

	/**
	 * Initialize area/association search
	 */
	initAreaSearch () {
		const container = document.querySelector('[data-area-search]')
		if (!container) return

		const form = container.querySelector('[data-area-search-form]')
		const input = container.querySelector('[data-area-search-input]')

		const results = container.querySelector('[data-area-search-results]')
		const list = container.querySelector('[data-area-search-list]')
		const empty = container.querySelector('[data-area-search-empty]')
		const dataEl = container.querySelector('[data-area-search-data]')

		if (!dataEl) return

		const items = JSON.parse(dataEl.textContent)

		const renderResults = (filtered) => {
			results.removeAttribute('hidden')
			list.innerHTML = ''

			if (filtered.length) {
				empty.setAttribute('hidden', '')
				filtered.forEach((item) => {
					const li = document.createElement('li')
					li.className = 'area-search__results-item'
					const a = document.createElement('a')
					a.className = 'area-search__results-link'
					a.href = item.url
					a.textContent = item.title
					li.appendChild(a)
					list.appendChild(li)
				})
			} else {
				empty.removeAttribute('hidden')
			}
		}

		const doSearch = () => {
			const query = input.value.trim().toLowerCase()

			if (!query.length) {
				results.setAttribute('hidden', '')
				return
			}

			const filtered = items.filter((item) =>
				item.title.toLowerCase().includes(query)
			)
			renderResults(filtered)
		}

		form.addEventListener('submit', (e) => {
			e.preventDefault()
			doSearch()
		})

		input.addEventListener('input', doSearch)

	}

}
